"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
const emptyDialogStack: readonly string[] = [];
let dialogStackSnapshot: readonly string[] = emptyDialogStack;
function pruneStaleDialogs() {
  if (typeof document === "undefined") return;
  const renderedDialogIds = new Set(Array.from(document.querySelectorAll<HTMLElement>("[data-dialog-stack-id]")).map(element => element.dataset.dialogStackId).filter((id): id is string => Boolean(id)));
  dialogStackSnapshot = dialogStackSnapshot.filter(id => renderedDialogIds.has(id));
}
function getRenderedStackElement(attribute: "dialogOverlayStackId" | "dialogStackId", id: string) {
  return Array.from(document.querySelectorAll<HTMLElement>(attribute === "dialogStackId" ? "[data-dialog-stack-id]" : "[data-dialog-overlay-stack-id]")).find(element => element.dataset[attribute] === id);
}
function syncRenderedDialogStack() {
  if (typeof document === "undefined") return;
  dialogStackSnapshot.forEach((id, index) => {
    const depth = dialogStackSnapshot.length - index - 1;
    const cappedDepth = Math.min(depth, 3);
    const content = getRenderedStackElement("dialogStackId", id);
    const overlay = getRenderedStackElement("dialogOverlayStackId", id);
    if (content) {
      content.dataset.dialogStackIndex = String(index);
      if (depth > 0) {
        content.dataset.dialogStackDepth = String(depth);
        content.dataset.dialogStacked = "true";
        content.style.scale = String(1 - cappedDepth * 0.015);
        content.style.translate = `0 -${cappedDepth * 8}px`;
      } else {
        delete content.dataset.dialogStackDepth;
        delete content.dataset.dialogStacked;
        content.style.removeProperty("scale");
        content.style.removeProperty("translate");
      }
    }
    if (overlay) {
      overlay.dataset.dialogStackIndex = String(index);
    }
  });
}
function registerDialog(id: string) {
  pruneStaleDialogs();
  if (dialogStackSnapshot.includes(id)) return () => undefined;
  dialogStackSnapshot = [...dialogStackSnapshot, id];
  syncRenderedDialogStack();
  return () => {
    if (!dialogStackSnapshot.includes(id)) return;
    dialogStackSnapshot = dialogStackSnapshot.filter(item => item !== id);
    syncRenderedDialogStack();
  };
}
export function useDialogStack() {
  const $ = _c(7);
  const id = React.useId();
  const unregisterRef = React.useRef(null);
  let t0;
  if ($[0] !== id) {
    t0 = node => {
      unregisterRef.current?.();
      unregisterRef.current = node ? registerDialog(id) : null;
    };
    $[0] = id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const contentRef = t0;
  const registeredIndex = dialogStackSnapshot.indexOf(id);
  const index = Math.max(registeredIndex, 0);
  const depth = registeredIndex < 0 ? 0 : dialogStackSnapshot.length - registeredIndex - 1;
  let t1;
  if ($[2] !== contentRef || $[3] !== depth || $[4] !== id || $[5] !== index) {
    t1 = {
      contentRef,
      depth,
      id,
      index
    };
    $[2] = contentRef;
    $[3] = depth;
    $[4] = id;
    $[5] = index;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}